/**
 * Completion Guard - Ensures tasks are properly completed
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Enforces documentation of remaining work before stopping
 * - Validates task completion criteria
 * - Generates handoff documentation automatically
 * - Prevents silent task abandonment
 */

import * as fs from 'fs';
import * as path from 'path';
import { TaskTracker, Task, TaskSummary, StopValidation, TaskCheckpoint } from './TaskTracker';

export interface CompletionCriteria {
  requireAllStepsComplete: boolean;
  requireNoBlockedSteps: boolean;
  requireResumptionNotes: boolean;
  minCompletionPercentage: number;
  requireFilesDocumented: boolean;
  requireNextStepsDocumented: boolean;
}

export interface CompletionResult {
  canComplete: boolean;
  violations: string[];
  suggestions: string[];
  requiredActions: RequiredAction[];
  handoffDocument?: string;
}

export interface RequiredAction {
  type: 'document' | 'complete' | 'unblock' | 'checkpoint';
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  targetStepId?: string;
}

export interface HandoffDocument {
  taskTitle: string;
  summary: string;
  completedWork: string[];
  remainingWork: string[];
  blockedItems: string[];
  contextNotes: string;
  resumptionInstructions: string;
  filesModified: string[];
  estimatedRemainingTime: string;
  createdAt: string;
  createdBy: string;
}

const DEFAULT_CRITERIA: CompletionCriteria = {
  requireAllStepsComplete: false,
  requireNoBlockedSteps: false,
  requireResumptionNotes: true,
  minCompletionPercentage: 0,
  requireFilesDocumented: true,
  requireNextStepsDocumented: true,
};

export class CompletionGuard {
  private workspaceRoot: string;
  private taskTracker: TaskTracker;
  private criteria: CompletionCriteria;
  private handoffPath: string;

  constructor(
    workspaceRoot: string,
    taskTracker: TaskTracker,
    criteria: Partial<CompletionCriteria> = {}
  ) {
    this.workspaceRoot = workspaceRoot;
    this.taskTracker = taskTracker;
    this.criteria = { ...DEFAULT_CRITERIA, ...criteria };
    this.handoffPath = path.join(workspaceRoot, '.guardrail', 'handoffs');
  }

  /**
   * Validate if work can be stopped
   * This is the CRITICAL check before any AI agent stops working
   */
  validateCompletion(): CompletionResult {
    const task = this.taskTracker.getCurrentTask();
    const violations: string[] = [];
    const suggestions: string[] = [];
    const requiredActions: RequiredAction[] = [];

    if (!task) {
      return {
        canComplete: true,
        violations: [],
        suggestions: ['Consider using task tracking for better progress visibility'],
        requiredActions: [],
      };
    }

    const summary = this.taskTracker.generateSummary();

    // Check completion percentage
    if (task.completionPercentage < this.criteria.minCompletionPercentage) {
      violations.push(
        `Task only ${task.completionPercentage}% complete (minimum: ${this.criteria.minCompletionPercentage}%)`
      );
      requiredActions.push({
        type: 'complete',
        description: `Complete more steps to reach ${this.criteria.minCompletionPercentage}% completion`,
        priority: 'high',
      });
    }

    // Check all steps complete
    if (this.criteria.requireAllStepsComplete && summary.remaining.length > 0) {
      violations.push(`${summary.remaining.length} steps still pending`);
      summary.remaining.forEach(step => {
        requiredActions.push({
          type: 'complete',
          description: step,
          priority: 'medium',
        });
      });
    }

    // Check in-progress steps
    if (summary.inProgress.length > 0) {
      violations.push(`${summary.inProgress.length} steps still in progress`);
      summary.inProgress.forEach(step => {
        requiredActions.push({
          type: 'document',
          description: `Document progress on: ${step}`,
          priority: 'critical',
        });
      });
    }

    // Check blocked steps
    if (this.criteria.requireNoBlockedSteps && summary.blocked.length > 0) {
      violations.push(`${summary.blocked.length} steps are blocked`);
      summary.blocked.forEach(step => {
        requiredActions.push({
          type: 'unblock',
          description: step,
          priority: 'high',
        });
      });
    }

    // Check if resumption notes are needed
    if (this.criteria.requireResumptionNotes && task.completionPercentage < 100) {
      const hasRecentCheckpoint = task.checkpoints.some(
        c => Date.now() - new Date(c.timestamp).getTime() < 5 * 60 * 1000 // 5 minutes
      );
      
      if (!hasRecentCheckpoint) {
        violations.push('No recent checkpoint with resumption notes');
        requiredActions.push({
          type: 'checkpoint',
          description: 'Create a checkpoint with resumption notes before stopping',
          priority: 'critical',
        });
      }
    }

    // Check files documented
    if (this.criteria.requireFilesDocumented) {
      const filesModified = task.steps
        .filter(s => s.status === 'completed')
        .flatMap(s => s.filesModified || []);
      
      if (filesModified.length === 0 && task.completionPercentage > 0) {
        suggestions.push('Document which files were modified in completed steps');
      }
    }

    // Check next steps documented
    if (this.criteria.requireNextStepsDocumented && summary.remaining.length > 0) {
      if (summary.nextSteps.length === 0) {
        violations.push('Next steps not documented');
        requiredActions.push({
          type: 'document',
          description: 'Document the next steps to be taken',
          priority: 'high',
        });
      }
    }

    // Generate handoff document if there are violations
    let handoffDocument: string | undefined;
    if (violations.length > 0) {
      handoffDocument = this.generateHandoffDocument(task, summary);
    }

    return {
      canComplete: violations.length === 0,
      violations,
      suggestions,
      requiredActions,
      handoffDocument,
    };
  }

  /**
   * Force create a handoff document before stopping
   */
  createHandoff(
    contextNotes: string,
    resumptionInstructions: string,
    agentId: string = 'unknown'
  ): HandoffDocument {
    const task = this.taskTracker.getCurrentTask();
    if (!task) {
      throw new Error('No active task to create handoff for');
    }

    const summary = this.taskTracker.generateSummary();

    const handoff: HandoffDocument = {
      taskTitle: task.title,
      summary: task.description,
      completedWork: summary.completed,
      remainingWork: [...summary.inProgress, ...summary.remaining],
      blockedItems: summary.blocked,
      contextNotes,
      resumptionInstructions,
      filesModified: task.steps
        .filter(s => s.filesModified)
        .flatMap(s => s.filesModified!),
      estimatedRemainingTime: summary.estimatedRemainingWork,
      createdAt: new Date().toISOString(),
      createdBy: agentId,
    };

    // Save handoff document
    this.saveHandoff(handoff, task.id);

    // Create checkpoint
    this.taskTracker.createCheckpoint(resumptionInstructions, {
      relevantVariables: { contextNotes },
    });

    return handoff;
  }

  /**
   * Generate handoff documentation markdown
   */
  generateHandoffDocument(task: Task, summary: TaskSummary): string {
    let doc = `# Task Handoff: ${task.title}\n\n`;
    doc += `**Task ID:** ${task.id}\n`;
    doc += `**Status:** ${task.status}\n`;
    doc += `**Progress:** ${task.completionPercentage}%\n`;
    doc += `**Generated:** ${new Date().toISOString()}\n\n`;

    doc += `## Description\n${task.description}\n\n`;

    doc += `## Progress Summary\n\n`;

    if (summary.completed.length > 0) {
      doc += `### ✅ Completed Work\n`;
      summary.completed.forEach(item => doc += `- ${item}\n`);
      doc += '\n';
    }

    if (summary.inProgress.length > 0) {
      doc += `### ⏳ Work In Progress (NEEDS ATTENTION)\n`;
      summary.inProgress.forEach(item => doc += `- ${item}\n`);
      doc += '\n';
    }

    if (summary.remaining.length > 0) {
      doc += `### 📋 Remaining Work\n`;
      summary.remaining.forEach(item => doc += `- ${item}\n`);
      doc += '\n';
    }

    if (summary.blocked.length > 0) {
      doc += `### 🚫 Blocked Items\n`;
      summary.blocked.forEach(item => doc += `- ${item}\n`);
      doc += '\n';
    }

    doc += `## Resumption Information\n\n`;
    doc += `**Estimated Remaining Time:** ${summary.estimatedRemainingWork}\n\n`;

    if (summary.nextSteps.length > 0) {
      doc += `### Next Steps\n`;
      summary.nextSteps.forEach((step, i) => doc += `${i + 1}. ${step}\n`);
      doc += '\n';
    }

    if (summary.warnings.length > 0) {
      doc += `### ⚠️ Warnings\n`;
      summary.warnings.forEach(w => doc += `- ${w}\n`);
      doc += '\n';
    }

    // Add checkpoint history
    if (task.checkpoints.length > 0) {
      const lastCheckpoint = task.checkpoints[task.checkpoints.length - 1];
      doc += `## Last Checkpoint\n`;
      doc += `**Created:** ${lastCheckpoint.timestamp}\n`;
      doc += `**Notes:** ${lastCheckpoint.resumptionNotes}\n\n`;
    }

    return doc;
  }

  /**
   * Validate and enforce completion with automatic handoff
   */
  enforceCompletion(
    contextNotes: string = '',
    agentId: string = 'unknown'
  ): {
    allowed: boolean;
    handoff?: HandoffDocument;
    message: string;
  } {
    const result = this.validateCompletion();

    if (result.canComplete) {
      return {
        allowed: true,
        message: 'Task completed successfully. All criteria met.',
      };
    }

    // Force create handoff for incomplete task
    const task = this.taskTracker.getCurrentTask();
    if (!task) {
      return {
        allowed: true,
        message: 'No active task.',
      };
    }

    const summary = this.taskTracker.generateSummary();
    const resumptionInstructions = this.generateResumptionInstructions(result, summary);

    const handoff = this.createHandoff(
      contextNotes || 'No additional context provided',
      resumptionInstructions,
      agentId
    );

    return {
      allowed: true, // Now allowed because we created the handoff
      handoff,
      message: `Task paused with handoff documentation. ${result.violations.length} items documented for next session.`,
    };
  }

  /**
   * Generate automatic resumption instructions
   */
  private generateResumptionInstructions(
    result: CompletionResult,
    summary: TaskSummary
  ): string {
    const instructions: string[] = [];

    instructions.push('## How to Resume This Task\n');

    if (summary.inProgress.length > 0) {
      instructions.push('### Continue In-Progress Work');
      instructions.push('The following items were being worked on:');
      summary.inProgress.forEach(item => instructions.push(`- ${item}`));
      instructions.push('');
    }

    if (summary.nextSteps.length > 0) {
      instructions.push('### Recommended Next Steps');
      summary.nextSteps.forEach((step, i) => instructions.push(`${i + 1}. ${step}`));
      instructions.push('');
    }

    if (summary.blocked.length > 0) {
      instructions.push('### Blocked Items to Resolve');
      summary.blocked.forEach(item => instructions.push(`- ${item}`));
      instructions.push('');
    }

    if (result.requiredActions.length > 0) {
      instructions.push('### Required Actions');
      result.requiredActions
        .filter(a => a.priority === 'critical' || a.priority === 'high')
        .forEach(action => {
          instructions.push(`- [${action.priority.toUpperCase()}] ${action.description}`);
        });
    }

    return instructions.join('\n');
  }

  /**
   * Save handoff document to disk
   */
  private saveHandoff(handoff: HandoffDocument, taskId: string): void {
    try {
      if (!fs.existsSync(this.handoffPath)) {
        fs.mkdirSync(this.handoffPath, { recursive: true });
      }

      const filename = `handoff-${taskId}-${Date.now()}.json`;
      const filepath = path.join(this.handoffPath, filename);
      fs.writeFileSync(filepath, JSON.stringify(handoff, null, 2));

      // Also save markdown version
      const mdFilename = `handoff-${taskId}-${Date.now()}.md`;
      const mdPath = path.join(this.handoffPath, mdFilename);
      const task = this.taskTracker.getCurrentTask();
      if (task) {
        const summary = this.taskTracker.generateSummary();
        fs.writeFileSync(mdPath, this.generateHandoffDocument(task, summary));
      }
    } catch (e) {
      console.error('Failed to save handoff:', e);
    }
  }

  /**
   * Get all handoff documents
   */
  getHandoffs(): HandoffDocument[] {
    const handoffs: HandoffDocument[] = [];

    try {
      if (fs.existsSync(this.handoffPath)) {
        const files = fs.readdirSync(this.handoffPath).filter(f => f.endsWith('.json'));
        for (const file of files) {
          const content = fs.readFileSync(path.join(this.handoffPath, file), 'utf-8');
          handoffs.push(JSON.parse(content));
        }
      }
    } catch (e) {
      console.error('Failed to load handoffs:', e);
    }

    return handoffs.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Get latest handoff for a task
   */
  getLatestHandoff(taskId: string): HandoffDocument | null {
    const handoffs = this.getHandoffs().filter(h => 
      h.taskTitle.includes(taskId) || 
      fs.readdirSync(this.handoffPath).some(f => f.includes(taskId))
    );
    return handoffs[0] || null;
  }

  /**
   * Update completion criteria
   */
  updateCriteria(criteria: Partial<CompletionCriteria>): void {
    this.criteria = { ...this.criteria, ...criteria };
  }

  /**
   * Get current criteria
   */
  getCriteria(): CompletionCriteria {
    return { ...this.criteria };
  }
}

/**
 * Create completion guard instance
 */
export function createCompletionGuard(
  workspaceRoot: string,
  taskTracker: TaskTracker,
  criteria?: Partial<CompletionCriteria>
): CompletionGuard {
  return new CompletionGuard(workspaceRoot, taskTracker, criteria);
}
